import type {
  CumulativeBundleSegment,
  CumulativeDomainSegment,
  InventoryBundle,
  InventoryDomain,
} from '@openx/types/gqlTypes';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { DomainTargeting, UrlTargetingType } from '@openx/types/targeting/domains';
import { TargetingFields, type TargetingState } from '@openx/types/targeting/targeting';
import { ComparisonType, Criteria, Intersect } from '@openx/types/targeting/targetingValuesTypes';
import type { DealPromptState } from '../../../constants';
import { type DealPromptFilters, dealPromptContextTypes } from '../../types';
import { setInventoryFormatTargetingFromMergedFilters } from './setInventoryFormatTargetingFromMergedFilters';

function mapToTargetingEnum(value: string[], enumMap: Record<string, string>): string {
  return value
    .map(v => enumMap[v.toLowerCase() as keyof typeof enumMap] || null)
    .filter((v): v is string => v !== null)
    .join(',');
}
const DS_VIDEO_PLACEMENT_TO_TARGETING_ENUM = {
  instream: '1',
  accompanying_content: '2',
  interstitial: '3',
  no_content_standalone: '4',
} as const;

export const convertDealPromptDataToDeal = (
  dealPromptResultSet: CumulativeDomainSegment | CumulativeBundleSegment,
  lastSubmittedFilterCriteria: AllTableCriteria<DealPromptFilters>,
  contextType: DealPromptState['contextType'],
  selectedItems?: InventoryDomain[] | InventoryBundle[]
): Partial<TargetingState> => {
  const targetingPreset: Partial<TargetingState> = {
    [TargetingFields.DIMENSION]: Criteria.ALL,
  };

  // Video placement/format targeting
  const videoPlacement = lastSubmittedFilterCriteria.filters?.ds_video_placement;
  if (videoPlacement?.length) {
    const placementValue = mapToTargetingEnum(videoPlacement, DS_VIDEO_PLACEMENT_TO_TARGETING_ENUM);
    if (placementValue) {
      targetingPreset[TargetingFields.VIDEO] = {
        video_plcmt: {
          op: Intersect.INTERSECTS,
          val: placementValue,
        },
      };
    }
  }

  // Context-specific targeting
  if (contextType === dealPromptContextTypes.APPS) {
    let appBundleIds: string[] = [];
    if (selectedItems && selectedItems.length > 0) {
      // Filter only selected bundles
      appBundleIds = (selectedItems as InventoryBundle[])
        .map(item => ('bundleId' in item ? item.bundleId : null))
        .filter((id): id is string => !!id);
    } else {
      const appBundlesSet = dealPromptResultSet as CumulativeBundleSegment;
      appBundleIds =
        appBundlesSet.results
          ?.map(item => ('appId' in item! ? item?.bundleId : null))
          .filter((id): id is string => !!id) || [];
    }
    if (appBundleIds.length) {
      targetingPreset[TargetingFields.APP_INVENTORY] = {
        app_bundle_id: {
          op: Criteria.ANY,
          val: appBundleIds.map(bundleId => ({
            op: ComparisonType.EQUALS,
            val: bundleId,
          })),
        },
      };
    }
  } else if (contextType === dealPromptContextTypes.DOMAINS) {
    let domainNames: string[] = [];
    if (selectedItems && selectedItems.length > 0) {
      // Filter only selected domains
      domainNames = (selectedItems as InventoryDomain[])
        .map(item => ('domainName' in item ? item.domainName : null))
        .filter((url): url is string => !!url);
    } else {
      const domainsResultSet = dealPromptResultSet as CumulativeDomainSegment;
      domainNames =
        domainsResultSet.results?.map(domain => domain?.domainName).filter((url): url is string => !!url) || [];
    }
    if (domainNames.length) {
      targetingPreset[TargetingFields.URL_TARGETING] = {
        domain_targeting_option: DomainTargeting.ALL_VARIATIONS,
        type: UrlTargetingType.whitelist,
        urls: domainNames,
      };
    }
  }

  // Geography targeting
  const countries = lastSubmittedFilterCriteria.filters?.geography;
  if (countries?.length) {
    targetingPreset[TargetingFields.GEOGRAPHIC] = {
      includes: {
        country: countries.join(',').toLowerCase(),
      },
    };
  }

  // Exclude indirect
  const isDirectEnabled = lastSubmittedFilterCriteria.filters?.ds_is_direct;
  if (isDirectEnabled && isDirectEnabled[0] === 'true') {
    targetingPreset[TargetingFields.EXCLUDE_INDIRECT] = true;
  }

  // Viewability
  const viewability = lastSubmittedFilterCriteria.filters?.ds_viewability;
  if (viewability) {
    targetingPreset[TargetingFields.VIEWABILITY] = {
      op: ComparisonType.GREATER_THAN_OR_EQUAL_TO,
      val: viewability,
    };
  }

  // Format & Device
  const updatedTargetingPresetWithFormatAndDevice = setInventoryFormatTargetingFromMergedFilters(
    targetingPreset,
    lastSubmittedFilterCriteria.filters,
    contextType
  );

  return updatedTargetingPresetWithFormatAndDevice;
};
